import {
  generateBreadcrumbList,
  getPreferredValue
} from '../utils';
import { HD_URL, SCHEMA_CTX } from '../constants';
import { formatProductLabel } from '../helpers/product-helpers';

// --- FAQ ---

const getFAQObject = (faqs = []) => {
  return faqs?.map(({ Answer, Question }) => ({
    '@type': 'Question',
    name: Question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: Answer,
    },
  }));
};

const generateFAQSchema = ({
  breadcrumbs = [],
  canonical = '',
  title = '',
  faqs = [],
}) => ({
  '@context': SCHEMA_CTX,
  '@type': 'FAQPage',
  name: title,
  url: canonical,
  breadcrumb: generateBreadcrumbList(breadcrumbs, { browse: true }),
  mainEntity: getFAQObject(faqs)
});

// --- Product / WebPage ---

const roundUpToTenth = (value) => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  return String(Math.ceil(num * 10) / 10);
};

const asPlainSku = (sku) => (sku?.toJSON ? sku.toJSON() : sku || {});
const replaceSize = (url) => (typeof url === 'string' ? url.replace('<SIZE>', '100') : url);

const toProduct = (skuData = {}) => {
  const {
    identifiers = {},
    reviews = {},
    pricing = {},
    media = {},
  } = skuData;

  const today = new Date(Date.now());

  // Keep internal checks numeric, but output reviewCount as a STRING
  const totalReviewsRaw = reviews?.ratingsReviews?.totalReviews;
  const totalReviewsNum = Number(totalReviewsRaw) || 0;
  const totalReviewsStr = String(totalReviewsNum);

  const averageRating = roundUpToTenth(reviews?.ratingsReviews?.averageRating);
  const hasRating = totalReviewsNum > 0 && !Number.isNaN(averageRating);

  const { value, original, mapDetail, promotion } = getPreferredValue(pricing, {});
  const { effectiveMap } = getPreferredValue(mapDetail, {});
  const finalPrice = getPreferredValue(effectiveMap, value, original);
  const hasStrikeThroughPrice = original && finalPrice !== original;
  const productName = formatProductLabel({ identifiers });

  return {
    '@type': 'Product',
    ...(hasRating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: averageRating,
        reviewCount: totalReviewsStr,
      },
    }),
    brand: { '@type': 'Thing',
      name: identifiers?.brandName },
    description: identifiers?.productLabel,
    image: replaceSize(media?.images?.[0]?.url),
    name: productName,
    offers: {
      '@type': 'Offer',
      url: identifiers?.canonicalUrl ? `${HD_URL}${identifiers.canonicalUrl}` : HD_URL,
      priceCurrency: 'USD',
      price: finalPrice,
      priceValidUntil: promotion?.dates?.end
        || `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`,
      ...(hasStrikeThroughPrice && {
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceType: `${SCHEMA_CTX}/StrikethroughPrice`,
          price: original,
          priceCurrency: 'USD'
        },
      }),
      // Currently for PLP pages, only In Stock products are shown.
      // If out of stock products are added in the future, availability checks can be updated here.
      availability: `${SCHEMA_CTX}/InStock`,
    },
    productID: identifiers?.itemId,
  };
};

const generateWebPageStructuredData = ({
  breadcrumbs = [],
  canonical = '',
  skus = [],
  title = '',
}) => {
  const schema = {
    '@context': SCHEMA_CTX,
    '@type': 'WebPage',
    name: title,
    url: canonical,
    breadcrumb: generateBreadcrumbList(breadcrumbs, { browse: true }),
  };

  if (skus && skus.length > 0) {
    schema.mainEntity = {
      '@type': 'WebPageElement',
      offers: {
        '@type': 'Offer',
        itemOffered: skus.map((sku) => toProduct(asPlainSku(sku))),
      },
    };
  }

  return schema;
};

/**
 * Generates structured data schemas for browse/search pages, including FAQ and WebPage schemas.
 *
 * @param {Object} params - The parameters object.
 * @param {Array<Object>} [params.breadcrumbs=[]] - Array of breadcrumb items.
 * @param {string} [params.canonical=''] - Canonical URL of the page.
 * @param {Object} [params.faqs={}] - FAQ data object, expected to have a `Questions` array.
 * @param {Array<Object>} [params.skus=[]] - Array of SKU objects.
 * @param {string} [params.title=''] - Title of the page.
 * @returns {Array<Object>} Array of structured data schemas.
 */
export const getBrowseSearchStructuredData = ({
  breadcrumbs = [],
  canonical = '',
  faqs = {},
  skus = [],
  title = '',
}) => {
  const schemas = [];
  const questions = faqs?.Questions || [];

  if (questions.length > 0) {
    schemas.push(
      generateFAQSchema({ breadcrumbs, canonical, faqs: questions, title })
    );
  }

  schemas.push(
    generateWebPageStructuredData({ breadcrumbs, canonical, skus, title })
  );

  return schemas;
};

/**
 * Generates structured data for EMT pages, including FAQ and breadcrumb schemas.
 *
 * @param {Object} params - The parameters object.
 * @param {Array<Object>} [params.faqs={}] - Array of FAQ objects.
 * @param {Object|Array<Object>} params.schema - Existing schema(s) to include.
 * @param {Array<Object>} [params.breadcrumbs=[]] - Array of breadcrumb items.
 * @returns {Array<Object>} Array of structured data schemas.
 */
export const getEmtStructuredData = ({ faqs = {}, schema, breadcrumbs = [] }) => {

  const isSchemaAnArray = Array.isArray(schema);
  const arraySchema = isSchemaAnArray ? schema : [schema];

  const hasFAQSchema = arraySchema.some(
    (schemaEl) => schemaEl?.['@type'] === 'FAQPage'
  );
  const hasFaqs = faqs?.length > 0;

  const newSchemas = [];

  if (!hasFAQSchema && hasFaqs) {
    newSchemas.push({
      '@context': SCHEMA_CTX,
      '@type': 'FAQPage',
      mainEntity: getFAQObject(faqs)
    });
  }

  newSchemas.push(generateBreadcrumbList(breadcrumbs, { browse: true }));

  return [...arraySchema, ...newSchemas];
};
